import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AuthShell } from "@/components/shared/AuthShell";
import { useNeutralAuthCover } from "@/hooks/useNeutralAuthCover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestPasswordReset } from "@/services/authService";

export function ForgotPasswordPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* The neutral cover, not a page-specific one: someone resetting a password
     has not picked a role, and its note ("¿Problemas para entrar? Escribinos.")
     is already the line this screen would have written for itself. */
  const cover = useNeutralAuthCover();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await requestPasswordReset(email.trim(), locale);
      setSubmitted(true);
    } catch {
      toast.error(t("auth.forgotPassword.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell cover={cover}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              {t("auth.forgotPassword.title", "Reset your password")}
            </h1>
            <p className="text-balance text-muted-foreground">
              {t(
                "auth.forgotPassword.success",
                "If an account exists with that email, we sent a password reset link."
              )}
            </p>
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">
              {t("auth.forgotPassword.backToLogin", "Back to login")}
            </Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell cover={cover}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("auth.forgotPassword.title", "Reset your password")}
          </h1>
          <p className="text-balance text-muted-foreground">
            {t(
              "auth.forgotPassword.subtitle",
              "Enter your email and we'll send you a link to set a new password."
            )}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">
              {t("auth.forgotPassword.email", "Email")}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          <Button type="submit" disabled={isSubmitting} className="transition-colors">
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("auth.forgotPassword.submitting", "Sending…")}
              </>
            ) : (
              t("auth.forgotPassword.submit", "Send reset link")
            )}
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">
              {t("auth.forgotPassword.backToLogin", "Back to login")}
            </Link>
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
